import Carousel, { ICarouselInstance } from 'react-native-reanimated-carousel';
import ChevronLeftThin from '@assets/icons/chevron-left-thin.svg';
import ChevronRightThin from '@assets/icons/chevron-right-thin.svg';
import i18n from '@/utils/i18n';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import { Analytics } from '@/utils/analytics';
import { APP_MODAL_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import { createStyles } from '@/contexts/theme-context';
import { fennelTrack } from '@/analytics/fennel/hook';
import { getTranslationValue } from '@/utils/translations';
import { ItemDetailsActionSheet } from '@/components/items/item-details-action-sheet';
import { ObiCarouselItem } from './obi-carousel-item';
import { ObiItem } from '@/types';
import { Platform, Text, TouchableOpacity, View } from 'react-native';
import { StackNavigationProp } from '@react-navigation/stack';
import { TrackableComponent } from '@/enums';
import { TYPOGRAPHY } from '@/styles';
import { useEffect, useRef, useState } from 'react';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useObiItemsOfInterest } from '@/hooks/use-obi-items-of-interest';

const OBI_PREVIOUS_DAYS = 30;
const OBI_NUM_ROWS = 3;
const ARROW_BUTTON_DIMENSION = 32;
const CAROUSEL_ITEM_HEIGHT = 56;
const CAROUSEL_BOTTOM_MARGIN = 16;
const CAROUSEL_ITEM_WIDTH = {
  LARGE_LAYOUT: {
    FULL: 0.36,
    SEMI: 0.28,
  },
  SMALL_LAYOUT: {
    FULL: 0.9,
    SEMI: 0.7,
  },
};

type NavigationType = StackNavigationProp<
  AppModalStackParams,
  APP_MODAL_ROUTES.ItemDetails
>;

interface Props {
  restaurantUUID: string;
  supplierUUID: string;
}

export const ObiItemsOfInterest: React.FC<Props> = ({
  supplierUUID,
  restaurantUUID,
}) => {
  const [styles, theme] = useStyles();
  const { appDimensions, isSmallLayout } = useLayoutContext();
  const { navigate } = useNavigation<NavigationType>();

  const carouselRef = useRef<ICarouselInstance>(null);

  const [variantPackUUID, setVariantPackUUID] = useState<string | null>(null);
  const [showActionSheet, setShowActionSheet] = useState(false);

  const { data, error, loading } = useObiItemsOfInterest(
    restaurantUUID,
    supplierUUID,
    OBI_PREVIOUS_DAYS,
    OBI_NUM_ROWS,
  );

  const onItemPress = (obiItem: ObiItem) => {
    if (Platform.OS === 'web') {
      setVariantPackUUID(obiItem.variant_pack_uuid);
      setShowActionSheet(true);
    } else {
      navigate(APP_MODAL_ROUTES.ItemDetails, {
        origin: TrackableComponent.OBI_ITEMS_OF_INTEREST,
        restaurantUUID,
        supplierUUID,
        variantPackUUID: obiItem.variant_pack_uuid,
      });
    }
    fennelTrack(AnalyticEvent.OBI_ITEM_PRESS, {
      message: getTranslationValue(obiItem.subtitle),
      obiItemType: obiItem.obi_item_type,
      restaurantUUID,
      supplierUUID,
      variantPackUUID: obiItem.variant_pack_uuid,
    });
  };

  const [carouselIndex, setCarouselIndex] = useState(0);
  const [width, setWidth] = useState(
    Math.floor(
      appDimensions.width *
        (isSmallLayout
          ? CAROUSEL_ITEM_WIDTH.SMALL_LAYOUT.SEMI
          : CAROUSEL_ITEM_WIDTH.LARGE_LAYOUT.SEMI),
    ),
  );

  // update the width when the app dimensions change or data is fetched
  useEffect(() => {
    if (appDimensions.width && data) {
      const adjustment = isSmallLayout
        ? data.length > 1
          ? CAROUSEL_ITEM_WIDTH.SMALL_LAYOUT.SEMI
          : CAROUSEL_ITEM_WIDTH.SMALL_LAYOUT.FULL
        : data.length > 1
          ? CAROUSEL_ITEM_WIDTH.LARGE_LAYOUT.SEMI
          : CAROUSEL_ITEM_WIDTH.LARGE_LAYOUT.FULL;
      setWidth(Math.floor(appDimensions.width * adjustment));
    }
  }, [isSmallLayout, appDimensions, data]);

  if (loading || error || !data || data.length === 0) {
    return null;
  }

  const onLeftArrowPress = () => {
    if (carouselIndex === 0) {
      return;
    }
    carouselRef.current?.prev({
      animated: true,
    });
  };

  const onRightArrowPress = () => {
    if (carouselIndex === data.length - 1) {
      return;
    }
    carouselRef.current?.next({
      animated: true,
    });
  };

  const onScrollEnd = () => {
    const idx = carouselRef.current?.getCurrentIndex();
    if (idx !== undefined) {
      setCarouselIndex(idx);
      Analytics.track('obi_items_interest.carousel.scroll', {
        properties: {
          index: idx,
          total: data.length,
        },
      });
    }
  };

  const isArrowLeftDisabled = carouselIndex === 0;
  const isArrowRightDisabled = carouselIndex === data.length - 1;
  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View>
          <Text style={[TYPOGRAPHY.h3, styles.title]}>
            {i18n.t('obi.items_of_interest.title')}
          </Text>
          <Text style={[TYPOGRAPHY.small, styles.subtitle]}>
            {i18n.t('obi.items_of_interest.past_days', {
              days: OBI_PREVIOUS_DAYS,
            })}
          </Text>
        </View>
        {!isSmallLayout && data.length > 1 ? (
          <View style={styles.actionsContainer}>
            <TouchableOpacity
              accessibilityLabel={i18n.t('obi.items_of_interest.scroll')}
              disabled={isArrowLeftDisabled}
              onPress={onLeftArrowPress}
              style={[
                styles.arrowButton,
                isArrowLeftDisabled ? styles.arrowButtonDisabled : null,
              ]}
            >
              <ChevronLeftThin
                stroke={theme.sk.color.icon.primary}
                width={ARROW_BUTTON_DIMENSION / 2}
              />
            </TouchableOpacity>
            <TouchableOpacity
              accessibilityLabel={i18n.t('obi.items_of_interest.scroll')}
              disabled={isArrowRightDisabled}
              onPress={onRightArrowPress}
              style={[
                styles.arrowButton,
                isArrowRightDisabled ? styles.arrowButtonDisabled : null,
              ]}
            >
              <ChevronRightThin
                stroke={theme.sk.color.icon.primary}
                width={ARROW_BUTTON_DIMENSION / 2}
              />
            </TouchableOpacity>
          </View>
        ) : null}
      </View>

      <Carousel
        data={data}
        height={CAROUSEL_ITEM_HEIGHT * data[0].length + CAROUSEL_BOTTOM_MARGIN}
        loop={false}
        onScrollEnd={onScrollEnd}
        ref={carouselRef}
        renderItem={({ item, index }) => (
          <View key={index} style={styles.carouselGrid}>
            {item.map((obiItem) => (
              <TouchableOpacity
                key={`${obiItem.obi_item_type}-${obiItem.title}-${obiItem.subtitle}-${obiItem.variant_pack_uuid}-container`}
                onPress={() => onItemPress(obiItem)}
              >
                <ObiCarouselItem
                  containerStyle={
                    data.length > 1 ? styles.paddedObiItem : undefined
                  }
                  eventTime={obiItem.event_time}
                  imageUrls={obiItem.variant_pack?.item.photo_url_list || []}
                  key={`${obiItem.obi_item_type}-${obiItem.title}-${obiItem.subtitle}-${obiItem.variant_pack_uuid}-item`}
                  obiType={obiItem.obi_item_type}
                  subtitle={obiItem.subtitle}
                  title={obiItem.title}
                  variantPackUUID={obiItem.variant_pack_uuid}
                />
              </TouchableOpacity>
            ))}
          </View>
        )}
        snapEnabled
        style={styles.carousel}
        width={width}
      />
      {variantPackUUID ? (
        <ItemDetailsActionSheet
          origin={TrackableComponent.OBI_ITEMS_OF_INTEREST}
          restaurantUUID={restaurantUUID}
          setShowActionSheet={(show) => {
            setShowActionSheet(show);
            if (!show) {
              setVariantPackUUID(null);
            }
          }}
          showActionSheet={showActionSheet}
          supplierUUID={supplierUUID}
          variantPackUUID={variantPackUUID}
        />
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  actionsContainer: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    marginTop: 12,
  },
  arrowButton: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderRadius: 16,
    height: ARROW_BUTTON_DIMENSION,
    justifyContent: 'center',
    width: ARROW_BUTTON_DIMENSION,
  },
  arrowButtonDisabled: {
    backgroundColor: theme.sk.color.bg.tertiary.default,
    opacity: 0.5,
  },
  carousel: {
    width: '100%',
  },
  carouselGrid: {
    gap: 8,
  },
  container: {
    gap: 16,
    marginLeft: 24,
    marginTop: 8,
  },
  header: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    marginRight: 24,
  },
  paddedObiItem: {
    marginRight: 24,
  },
  subtitle: {
    color: theme.sk.color.text.tertiary,
  },
  title: {
    marginVertical: 8,
  },
}));
